import React, { useEffect, useId, useRef, useState } from 'react';
import { TIME_STEP_OPTIONS, clockLabel, parseClock, toClock } from '../../../lib/events/foundation';

// M19 T4: one time control for every Events time. Type ("4:30 pm", "1630") or scroll the list, in 5-minute steps; arrow keys move 5 minutes.
// Value is "HH:MM" or ''. A blank stays blank: this control never fills in the current time. Flight times keep the exact minute (exact).
export default function TimeCombo({ value, onChange, label, id, exact = false, disabled = false, className = '', inputClassName = '' }) {
    const generated = useId();
    const inputId = id || `time-${generated}`;
    const clean = String(value || '').slice(0, 5);
    const [text, setText] = useState(clean ? clockLabel(clean) : '');
    const [open, setOpen] = useState(false);
    const [bad, setBad] = useState(false);
    const box = useRef(null);
    const list = useRef(null);
    const typing = useRef(false);

    useEffect(() => { if (!typing.current) setText(clean ? clockLabel(clean) : ''); }, [clean]);
    useEffect(() => {
        if (!open) return undefined;
        const near = list.current?.querySelector('[aria-selected="true"]') || list.current?.querySelector('[data-hour="8"]');
        near?.scrollIntoView?.({ block: 'center' });
        const away = (event) => { if (box.current && !box.current.contains(event.target)) setOpen(false); };
        document.addEventListener('pointerdown', away);
        return () => document.removeEventListener('pointerdown', away);
    }, [open]);

    const commit = () => {
        typing.current = false;
        if (!text.trim()) { setBad(false); if (clean) onChange(''); return; }
        const minutes = parseClock(text, { step: exact ? 1 : 5 });
        if (minutes === null) { setBad(true); return; }
        setBad(false);
        const next = toClock(minutes);
        setText(clockLabel(next));
        if (next !== clean) onChange(next);
    };
    const choose = (next) => { setOpen(false); setBad(false); setText(clockLabel(next)); onChange(next); };
    const nudge = (by) => {
        const current = parseClock(text, { step: 1 });
        const base = current === null ? 8 * 60 : current;
        choose(toClock(((Math.round(base / 5) * 5 + by) % 1440 + 1440) % 1440));
    };

    return (
        <span className={`ef-time ${className}`.trim()} ref={box}>
            <input id={inputId} className={inputClassName} type="text" inputMode="text" autoComplete="off" role="combobox" aria-label={label} aria-expanded={open} aria-controls={`${inputId}-list`} aria-invalid={bad}
                placeholder="e.g. 4:30 pm" value={text} disabled={disabled} data-testid={`time-${label}`}
                onFocus={() => { typing.current = true; }}
                onChange={(event) => {
                    const typed = event.target.value;
                    setText(typed); setBad(false);
                    // A finished time ("4:30 pm", "16:30", "1630") counts straight away, so a save right after typing never loses it.
                    if (/^\s*(\d{1,2}:\d{2}|\d{3,4})\s*([ap]\.?m?\.?)?\s*$/i.test(typed)) {
                        const minutes = parseClock(typed, { step: exact ? 1 : 5 });
                        if (minutes !== null && toClock(minutes) !== clean) onChange(toClock(minutes));
                    } else if (!typed.trim() && clean) onChange('');
                }}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') { event.preventDefault(); nudge(5); }
                    else if (event.key === 'ArrowUp') { event.preventDefault(); nudge(-5); }
                    else if (event.key === 'Escape') setOpen(false);
                    else if (event.key === 'Enter') { event.preventDefault(); commit(); }
                }} />
            <button type="button" className="ef-time__toggle" aria-label={`Choose ${label} from a list`} aria-expanded={open} disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => setOpen((v) => !v)}>▾</button>
            {open && (
                <ul id={`${inputId}-list`} role="listbox" aria-label={label} className="ef-time__list" ref={list}>
                    {TIME_STEP_OPTIONS.map((option) => (
                        <li key={option.value} role="option" aria-selected={option.value === clean} data-hour={Number(option.value.slice(0, 2))}>
                            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option.value)}>{option.label}</button>
                        </li>
                    ))}
                </ul>
            )}
            {bad && <span role="alert" className="ef-time__bad">Enter a time like 4:30 pm or 1630.</span>}
        </span>
    );
}
